import { DownOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { DATE_FORMAT } from "localConstants.ts";
import { PropsWithChildren, useRef, useState, useImperativeHandle, forwardRef, RefObject, Ref } from "react";
import { MouseEvent } from "react";

import "components/UI/Table/UI/InteractiveCell/miscs/overrides.css";

export type FieldValueType = string | number | bigint | readonly string[] | undefined | Dayjs | null;

interface FieldWrapperProps<TValue extends FieldValueType> {
    displayValue: TValue;
    isError?: boolean;
    errorMessage?: string;
    isSelect?: boolean;
}

export interface FieldWrapperImperativeHandle {
    setIsEditMode: (v: boolean) => void;
    setBlockTriggerOnHover: (v: boolean) => void;
    triggeredByClick: boolean;
    setTriggeredByClick: (v: boolean) => void;
    setIsFocused: (v: boolean) => void;
    elementRef: RefObject<HTMLDivElement>;
}

export const FieldWrapper = forwardRef(function InteractiveCell<TValue extends FieldValueType>(
    { displayValue, children, isError, errorMessage, isSelect }: PropsWithChildren<FieldWrapperProps<TValue>>,
    ref: Ref<FieldWrapperImperativeHandle>
) {
    const elementRef = useRef<HTMLDivElement>(null);

    const [isFocus, setIsFocus] = useState<boolean>(false);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [triggeredByClick, setTriggeredByClick] = useState<boolean>(false);
    const [, setBlockTriggerOnHover] = useState<boolean>(false);

    useImperativeHandle(ref, () => ({
        setIsEditMode,
        setBlockTriggerOnHover,
        triggeredByClick,
        setTriggeredByClick,
        setIsFocused: setIsFocus,
        elementRef,
    }));

    const handleValueKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
        if (isEditMode) {
            return;
        }

        if (e.key === "Enter" || e.key === "Space") {
            setIsEditMode(true);
            e.preventDefault();
            setIsEditMode(true);
            setBlockTriggerOnHover(true);
            setTriggeredByClick(true);
        }
    };

    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
        if (isEditMode) {
            return;
        }

        event?.stopPropagation();
        setIsEditMode(true);
        setBlockTriggerOnHover(true);
        setTriggeredByClick(true);
    };

    const handleMouseEnter = () => {
        // Click-only: don't trigger edit mode on hover
    };

    const handleMouseLeave = () => {
        if (!isFocus && !triggeredByClick && (displayValue !== undefined || displayValue !== null)) {
            setTimeout(() => setIsEditMode(false), 200);
        }

        setBlockTriggerOnHover(false);
    };

    // TODO refactor text-[#008080] to class (whole project color refactor)
    return (
        <div
            ref={elementRef}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={`relative flex flex-1 items-center w-full h-full rounded hover:bg-neutral-200 text-[#008080]`}
        >
            {/* Always render display value to maintain consistent row height */}
            <span
                role="button"
                tabIndex={0}
                onKeyDown={handleValueKeyDown}
                className={`appearance-none bg-transparent border border-transparent min-w-full text-left focus:outline-none focus:border-0 focus:border-[#008080] focus:rounded p-1
    ${isEditMode ? "invisible" : ""}
    ${isError ? "border-2 border-[#EF4444] rounded" : ""}
   `}
            >
                <div className={"flex flex-col w-full"}>
                    <div className={"flex items-center justify-between w-full"}>
                        <span>{resolveValue(displayValue)}  {isSelect && <DownOutlined className="ml-2 text-xs" />}</span>
                    </div>
                    <span className={"text-[#EF4444]"}>{errorMessage && errorMessage}</span>
                </div>
            </span>
            {/* Edit controls positioned absolutely to prevent row height jump */}
            {isEditMode && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 z-10 min-w-full">
                    {children}
                </div>
            )}
        </div>
    );
});

const resolveValue = (displayValue: FieldValueType) => {
    if (dayjs.isDayjs(displayValue)) {
        return dayjs(displayValue).format(DATE_FORMAT);
    }

    if (typeof displayValue === "bigint") {
        return displayValue.toString();
    }

    if (displayValue === null || displayValue === undefined) {
        return "-";
    }

    return displayValue;
};
